import { useCallback, useEffect, useState } from "react";
import { Send, Trash2, AlertTriangle } from "lucide-react";
import { Select } from "antd";

import { Button } from "../ui/button";
import { Input, Textarea } from "../ui/input";
import ToastMsg from "../common/ToastMsg";
import useApiMethods from "../../lib/useApiMethods";
import { APPRAISAL_KPI_METRICS, APPRAISAL_REVISE_GOALS } from "../../constants/api";

/**
 * The manager's answer to a disputed goal set.
 *
 * Only shown while the appraisal sits at goals_sent_back. The whole set is
 * replaced rather than patched, because a dispute is usually about the shape
 * of the set and a partial edit cannot hold the total at 100%.
 */
const GoalRevisePanel = ({ appraisal, goals = [], onUpdated }) => {
    const { fetch, update } = useApiMethods();
    const [kpis, setKpis] = useState([]);
    const [rows, setRows] = useState([]);
    const [comment, setComment] = useState("");
    const [saving, setSaving] = useState(false);

    const sentBack = appraisal?.status === "goals_sent_back";

    const loadKpis = useCallback(async () => {
        try {
            const res = await fetch(APPRAISAL_KPI_METRICS);
            setKpis(res?.data || []);
        } catch (_) {
            setKpis([]);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => { if (sentBack) loadKpis(); }, [sentBack, loadKpis]);

    // Seed from the disputed set so the manager edits what the employee saw,
    // rather than rebuilding it from nothing.
    useEffect(() => {
        setRows(goals.map((g) => ({
            kpi_id: g.kpi_metric_id ?? g.kpi_id ?? null,
            kpi_name: g.kpi_name,
            weightage: g.weightage ?? 0,
            expected_outcome: g.expected_outcome || "",
        })));
    }, [goals]);

    if (!sentBack) return null;

    const total = rows.reduce((sum, r) => sum + (Number(r.weightage) || 0), 0);
    const chosen = new Set(rows.map((r) => r.kpi_id));

    const addKpi = (kpiId) => {
        const kpi = kpis.find((k) => k.id === kpiId);
        if (!kpi || chosen.has(kpiId)) return;
        setRows((rs) => [...rs, {
            kpi_id: kpi.id,
            kpi_name: kpi.name,
            weightage: kpi.default_weightage ?? 0,
            expected_outcome: "",
        }]);
    };

    const patch = (kpiId, next) =>
        setRows((rs) => rs.map((r) => (r.kpi_id === kpiId ? { ...r, ...next } : r)));

    const submit = async () => {
        if (!rows.length) {
            ToastMsg("error", "Add at least one goal");
            return;
        }
        if (rows.some((r) => !r.kpi_id)) {
            ToastMsg("error", "One of these goals is not linked to a KPI. Remove it and add it again.");
            return;
        }
        if (Math.abs(total - 100) > 0.01) {
            ToastMsg("error", `Weightages must total 100%. Current total: ${total}%.`);
            return;
        }
        setSaving(true);
        try {
            const res = await update(APPRAISAL_REVISE_GOALS, {
                id: appraisal.id,
                comment: comment.trim(),
                goals: rows.map((r) => ({
                    kpi_id: r.kpi_id,
                    weightage: Number(r.weightage) || 0,
                    expected_outcome: r.expected_outcome || "",
                })),
            });
            if (res?.status === "success") {
                ToastMsg("success", res.message || "Goals re-sent for agreement");
                setComment("");
                onUpdated?.(res.data);
            } else {
                ToastMsg("error", res?.message || "Could not re-send the goals");
            }
        } catch (_) {
            ToastMsg("error", "Could not re-send the goals");
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="rounded-md border border-orange-200 dark:border-orange-900/40 bg-orange-50 dark:bg-orange-900/20 p-3 sm:p-4 space-y-3">
            <div>
                <p className="text-sm font-medium text-orange-900 dark:text-orange-200 flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4" /> Goals sent back by {appraisal.employee?.name || "the employee"}
                </p>
                {appraisal.goals_dispute_reason && (
                    <p className="text-xs text-orange-700 dark:text-orange-300 mt-1">
                        They asked for: &ldquo;{appraisal.goals_dispute_reason}&rdquo;
                    </p>
                )}
            </div>

            <div className="space-y-2">
                {rows.map((r) => (
                    <div key={r.kpi_id ?? r.kpi_name}
                        className="rounded-md border border-border bg-white dark:bg-gray-800 p-3 space-y-2">
                        <div className="flex items-start justify-between gap-2">
                            <span className="text-sm font-medium">{r.kpi_name}</span>
                            <button type="button" className="text-red-500 hover:text-red-600"
                                onClick={() => setRows((rs) => rs.filter((x) => x.kpi_id !== r.kpi_id))}>
                                <Trash2 className="w-4 h-4" />
                            </button>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                            <div>
                                <label className="block text-xs font-medium mb-1">Weightage %</label>
                                <Input type="number" min={0} max={100}
                                    value={r.weightage ?? ""}
                                    onChange={(e) => patch(r.kpi_id, { weightage: Number(e.target.value) || 0 })} />
                            </div>
                            <div className="sm:col-span-2">
                                <label className="block text-xs font-medium mb-1">Expected outcome</label>
                                <Input placeholder="What good looks like"
                                    value={r.expected_outcome || ""}
                                    onChange={(e) => patch(r.kpi_id, { expected_outcome: e.target.value })} />
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 items-end">
                <div>
                    <label className="block text-xs font-medium mb-1">Add a KPI</label>
                    <Select
                        showSearch
                        optionFilterProp="label"
                        placeholder="Select a KPI"
                        value={null}
                        onChange={addKpi}
                        className="w-full"
                        options={kpis
                            .filter((k) => !chosen.has(k.id))
                            .map((k) => ({ value: k.id, label: `${k.name} (${k.default_weightage ?? 0}%)` }))}
                    />
                </div>
                <p className={`text-xs ${Math.abs(total - 100) > 0.01 ? "text-red-500" : "text-green-600"}`}>
                    Total weightage: {total}% {Math.abs(total - 100) > 0.01 ? "— must be 100%" : "✓"}
                </p>
            </div>

            <Textarea
                rows={2}
                placeholder="Optional note back to them, e.g. split the patient-feedback KPI in two as discussed."
                value={comment}
                onChange={(e) => setComment(e.target.value)}
            />

            <Button size="sm" variant="brand" disabled={saving || Math.abs(total - 100) > 0.01}
                onClick={submit}>
                <Send className="w-4 h-4 mr-1" /> Re-send for agreement
            </Button>
        </div>
    );
};

export default GoalRevisePanel;
